import React, { ReactNode } from 'react';
import I18n from 'i18n-js';
import ReactModal from 'react-modal';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';

interface PrimaryButton {
  text: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  variant?: 'purple' | 'red';
}

interface SecondaryButton {
  text: string;
  onClick: () => void;
  variant?: 'purple' | 'red';
}

interface Headline {
  id: string;
  text: string;
}

interface Props {
  body: ReactNode;
  headline: Headline;
  isOpen: boolean;
  primaryButton: PrimaryButton;
  icon?: ReactNode;
  secondaryButton?: SecondaryButton;
  onClose?: () => void;
}

const DEFAULT_ICON = <Icon color="tci-purple" icon="Live" size={30} />;

// AIDEV-NOTE: Shared dialog primitive for Teacher-Paced Mode flows (student join/end popups
// and the teacher end-session confirmation). Pass `onClose` to enable the top-right X and
// overlay-close; pass `secondaryButton` to add a Cancel-style button alongside the primary;
// pass `icon` to swap the centerpiece icon (default is the Live glyph). Primary button
// states: `loading` shows a spinner + disables; `variant: 'red'` switches to destructive
// tone.
const SessionPopup = ({
  body,
  headline,
  icon = DEFAULT_ICON,
  isOpen,
  primaryButton,
  secondaryButton,
  onClose,
}: Props) => {
  const dismissable = Boolean(onClose);
  const isPrimaryDisabled = Boolean(primaryButton.disabled || primaryButton.loading);
  const primaryVariant = primaryButton.variant ?? 'purple';
  const showSecondary = Boolean(secondaryButton);

  return (
    <ReactModal
      shouldFocusAfterRender
      shouldReturnFocusAfterClose
      aria={{ describedby: `${headline.id}-body`, labelledby: headline.id }}
      className={cn(
        'tw-relative tw-bg-white tw-rounded-lg tw-shadow-custom tw-outline-none tw-box-border',
        'tw-mx-auto tw-my-auto tw-p-8 tw-max-w-md tw-w-full tw-aspect-[1.5]',
      )}
      isOpen={isOpen}
      overlayClassName={cn(
        'tw-fixed tw-inset-0 tw-z-9999 tw-bg-black/50',
        'tw-flex tw-items-center tw-justify-center tw-p-4',
      )}
      role="dialog"
      shouldCloseOnOverlayClick={dismissable}
      onRequestClose={onClose}
    >
      {dismissable && (
        <button
          aria-label={I18n.t('close_dialog')}
          className={cn(
            'tw-absolute tw-top-4 tw-right-4 tw-bg-transparent tw-border-none tw-p-1 tw-text-dark-grey',
            'tw-cursor-pointer tw-rounded-full tw-inline-flex tw-items-center tw-justify-center',
          )}
          type="button"
          onClick={onClose}
        >
          <Icon icon="Close" size={20} />
        </button>
      )}
      <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
        <div
          aria-hidden="true"
          className={cn(
            'tw-w-14 tw-h-14 tw-rounded-full tw-bg-tci-purple/5 tw-mb-4',
            'tw-flex tw-items-center tw-justify-center tw-text-tci-purple',
          )}
        >
          {icon}
        </div>
        <Text asChild variant="heading2" weight="bold">
          <h1 className="tw-text-tci-black tw-mb-2 tw-font-tci" id={headline.id}>
            {headline.text}
          </h1>
        </Text>
        <Text asChild className="tw-text-tci-black tw-mb-10 tw-font-tci" variant="body">
          <div id={`${headline.id}-body`}>{body}</div>
        </Text>
        <div
          className={cn(
            'tw-flex tw-gap-3',
            showSecondary ? 'tw-flex-row tw-justify-center tw-items-center' : 'tw-flex-col',
          )}
        >
          {secondaryButton && (
            <button
              className={cn(
                secondaryButton.variant === 'red'
                  ? 'btn btn--red tw-py-2 tw-text-xl tw-rounded-md'
                  : 'btn btn--link-purple tw-text-lg tw-rounded-md',
              )}
              type="button"
              onClick={secondaryButton.onClick}
            >
              {secondaryButton.text}
            </button>
          )}
          <button
            className={cn(
              'btn tw-py-2 tw-text-xl tw-rounded-md',
              !showSecondary && 'tw-px-14',
              primaryVariant === 'red' ? 'btn--red' : 'btn--purple',
              isPrimaryDisabled && 'tw-opacity-50 tw-cursor-not-allowed',
            )}
            disabled={isPrimaryDisabled}
            type="button"
            onClick={primaryButton.onClick}
          >
            {primaryButton.loading && (
              <i aria-hidden="true" className="fa fa-spinner fa-spin tw-mr-2" />
            )}
            {primaryButton.text}
          </button>
        </div>
      </div>
    </ReactModal>
  );
};

export default SessionPopup;
